import React from 'react';
import styled from 'styled-components';
import { StatusChip } from '../components/StatusChip';
import { Card, CardHead, INK } from '../onboarding/review/reviewStyles';
import { fmtDate, fmtMoney, fmtPct, fmtSigned } from '../presentation/format';
import type { BridgeLine, BridgeLineKind, CollectionPeriod } from './periodApi';

/**
 * The borrowing-base bridge for a period (F-LR-047, F-LR-052): eligible, times the advance rate,
 * less each concentration cut and the reserve, to the base. Deductions read "less …" and carry
 * their sign; the Prior and Change columns appear only when a prior run exists; the format comes
 * from the line's kind, not from a "%" in its label.
 */

const Table = styled.table`
  width: 100%; border-collapse: collapse; font-size: 13px;
  th, td { padding: 8px 10px; text-align: left; border-bottom: 1px solid ${INK.line}; vertical-align: top; }
  th { color: ${INK.faint}; font-size: 11px; font-weight: 750; letter-spacing: .06em; text-transform: uppercase; }
  td.num, th.num { text-align: right; font-variant-numeric: tabular-nums; white-space: nowrap; }
  tr[data-kind='RESULT'] td { font-weight: 700; color: ${INK.strong}; border-top: 1px solid ${INK.lineStrong}; }
  tr[data-kind='DEDUCTION'] td:first-child { padding-left: 22px; }
  small { display: block; color: ${INK.faint}; font-weight: 400; }
`;

/** The kind the line states, or the one its label implied before the payload carried one. */
export const bridgeLineKind = (line: BridgeLine): BridgeLineKind =>
  line.kind ?? (line.label.includes('%') ? 'RATE' : line.label === 'Borrowing base' ? 'RESULT' : 'TOTAL');

export const bridgeLineAmount = (line: BridgeLine): number | null =>
  line.amount ?? line.value ?? null;

/** "less concentration excess" for a deduction; the label as written otherwise. */
export const bridgeLineLabel = (line: BridgeLine): string => {
  if (bridgeLineKind(line) !== 'DEDUCTION') return line.label;
  const label = line.label.replace(/^less\s+/i, '');
  return `less ${label.charAt(0).toLowerCase()}${label.slice(1)}`;
};

const cell = (kind: BridgeLineKind, value: number | null | undefined, currency: string): string => {
  if (value === null || value === undefined) return '—';
  if (kind === 'RATE') return fmtPct(value, 2);
  if (kind === 'DEDUCTION') return value === 0 ? fmtMoney(0, currency) : fmtSigned(-Math.abs(value), { currency });
  return fmtMoney(value, currency);
};

const change = (line: BridgeLine, kind: BridgeLineKind, currency: string): string => {
  if (line.delta === null || line.delta === undefined) return '—';
  const base = fmtSigned(line.delta, kind === 'RATE' ? { kind: 'pct' } : { currency });
  return line.deltaPct !== null && line.deltaPct !== undefined && kind !== 'RATE' ? `${base} (${fmtSigned(line.deltaPct, { kind: 'pct', digits: 1 })})` : base;
};

const BridgeCard: React.FC<{ period: CollectionPeriod; currency?: string | null }> = ({ period, currency }) => {
  const run = period.run!;
  const ccy = currency || 'USD';
  const lines = run.bridge?.lines || [];
  const showPrior = lines.some(line => line.prior !== null && line.prior !== undefined);
  return <Card>
    <CardHead>
      <div><h2>Borrowing base</h2><p>Computed {fmtDate(run.createdAt)} from the released tape{showPrior && run.bridge.priorPeriodLabel ? ` · compared with period ${run.bridge.priorPeriodLabel}` : showPrior ? '' : ' · first computed period, nothing prior to compare'}</p></div>
      <StatusChip tone={run.approvalStatus === 'APPROVED' ? 'done' : 'progress'} label={run.approvalStatus === 'APPROVED' ? 'Approved' : 'Candidate'} />
    </CardHead>
    {lines.length === 0 ? <p style={{ color: INK.muted }}>The run recorded no bridge lines.</p> : <Table>
      <thead><tr><th>Bridge</th><th className="num">This period</th>{showPrior && <><th className="num">Prior</th><th className="num">Change</th></>}</tr></thead>
      <tbody>{lines.map((line, index) => {
        const kind = bridgeLineKind(line);
        return <tr key={line.key || `${line.label}-${index}`} data-kind={kind}>
          <td>{bridgeLineLabel(line)}{line.detail ? <small>{line.detail}</small> : null}</td>
          <td className="num">{cell(kind, bridgeLineAmount(line), ccy)}</td>
          {showPrior && <>
            <td className="num">{cell(kind, line.prior, ccy)}</td>
            <td className="num" style={{ color: line.delta === null || line.delta === undefined ? INK.faint : line.delta < 0 ? '#f0a24a' : '#7cddbe' }}>{change(line, kind, ccy)}</td>
          </>}
        </tr>;
      })}</tbody>
    </Table>}
  </Card>;
};

export default BridgeCard;
